import buildConfig from '../build-config.json';
import { ErrorNotifier } from '../error-notifier';
import { ActionMeasurement } from '../message.types';
import { TriggeredEventPropertyValue } from '../triggered-event.types';
import { fetch } from '../utility/fetch';
import { HTTP_STATUS } from './constants';
import { SocialProofSchema as SocialProofResponseJSON } from '../generated/repro.rails/v1-internal-web-social_proof';

interface SocialProofRequestParams {
  messageId: string;
  token: string;
  eventPropertyValue: TriggeredEventPropertyValue;
}

type SocialProofApiResponse = {
  rawHtml: string;
  rawCss: string;
  rawJavaScript: string;
  thresholdExceeded: boolean;
  measurements: ActionMeasurement[];
  closeButtonClickTrigger: string;
};

export const fetchSocialProofMessage = async (params: SocialProofRequestParams): Promise<SocialProofApiResponse> => {
  if (!navigator.onLine) {
    throw new Error('Aborting SocialProof API call due to browser being offline.');
  }

  const searchParams = new URLSearchParams();
  searchParams.set('token', params.token);
  searchParams.set('encoded_id', params.messageId);
  searchParams.set('event_property_value', `${params.eventPropertyValue}`);

  const url = new URL(buildConfig.socialProofMessageURL);
  url.search = searchParams.toString();

  const response = await fetch(url.href, {
    headers: { 'Content-Type': 'application/json' },
    mode: 'cors',
  });

  if (response.status === HTTP_STATUS.UNAUTHORIZED) {
    throw new Error('SocialProof API call failed due to authentication error. Check your repro token.');
  }
  if (!response.ok) {
    throw new Error(`SocialProof API call failed with status ${response.status}.`);
  }

  const json: SocialProofResponseJSON = await response.json();
  if (typeof json !== 'object') {
    throw new Error(`Invalid response from SocialProof API: ${json}`);
  }
  if (!json.success) {
    throw new Error('SocialProof API call failed due to unexpected error.');
  }
  const { message } = json.results;
  if ('error' in message) {
    throw new Error(`Error from SocialProof API: ${message.error}`);
  }

  try {
    return {
      rawHtml: message.raw_html ?? '',
      rawCss: message.raw_css ?? '',
      rawJavaScript: message.raw_javascript ?? '',
      thresholdExceeded: message.is_threshold_exceeded,
      measurements: message.measurements,
      closeButtonClickTrigger: message.close_button_click_trigger,
    };
  } catch (error: any) {
    ErrorNotifier.notify({
      error,
      extraPayload: {
        requestParams: searchParams.toString(),
        apiResponse: json,
      },
    });
    throw error;
  }
};
